import { useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  firstCompany,
  useMyClients,
} from "@/features/clients/hooks/use-clients";

/**
 * Hook to handle automatic redirects based on company state
 * - Redirects to /start if no companies exist
 * - Redirects to /start if current company in URL is invalid
 * - Allows navigation to /start, /companies, /account even without companies
 */
export function useCompanyRedirect() {
  const navigate = useNavigate();
  const { company: clientId } = useParams<{ company: string }>();
  const location = useLocation();
  const { data: clients = [], isLoading } = useMyClients();

  useEffect(() => {
    // Don't redirect while loading
    if (isLoading) return;

    // Allow these routes even without companies
    const allowedWithoutCompany = [
      "/start",
      "/companies",
      "/account",
      "/logout",
    ];
    if (
      allowedWithoutCompany.some((path) => location.pathname.startsWith(path))
    ) {
      return;
    }

    // If no companies exist, redirect to start
    if (clients.length === 0) {
      navigate("/start", { replace: true });
      return;
    }

    // If we're in a company route, verify the company exists
    if (clientId) {
      const companyExists = clients.some((c) => c.client.id === clientId);
      if (!companyExists) {
        // Company doesn't exist, redirect to first company or start
        if (clients.length > 0) {
          navigate(`/${firstCompany(clients)!.client.id}/home`, {
            replace: true,
          });
        } else {
          navigate("/start", { replace: true });
        }
      }
    }
  }, [clients, clientId, isLoading, navigate, location.pathname]);

  return { clients, isLoading };
}
